import { Fragment } from "react";
import {
  FlaskConical,
  Camera,
  ScanSearch,
  Brain,
  FileCheck,
  ArrowRight,
  Sparkles,
  Cloud,
  Wifi
} from "lucide-react";

const STAGES = [
  {
    id: 1,
    icon: FlaskConical,
    label: "Device",
    short: "Hardware",
    title: "Patented Reagent Wells",
    desc: "Custom chemistry reacts with urine to reveal protein, albumin & creatinine levels — no strips, no eye-balling.",
    color: "amber",
    bg: "bg-amber-50",
    border: "border-amber-200",
    iconBg: "bg-amber-100",
    iconText: "text-amber-700",
    badge: "Patented"
  },
  {
    id: 2,
    icon: Camera,
    label: "Smartphone Camera",
    short: "Capture",
    title: "Your Phone Becomes the Lab",
    desc: "Open the app, point your camera at the device. Works on any Android 9.0+ smartphone — no extra hardware.",
    color: "cyan",
    bg: "bg-cyan-50",
    border: "border-cyan-200",
    iconBg: "bg-cyan-100",
    iconText: "text-cyan-700",
    badge: "Mobile App"
  },
  {
    id: 3,
    icon: ScanSearch,
    label: "Computer Vision",
    short: "Image Processing",
    title: "Smart Image Correction",
    desc: "Auto-corrects for lighting, angle, and color cast. Detects each reagent well precisely and extracts pure color signals.",
    color: "indigo",
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    iconBg: "bg-indigo-100",
    iconText: "text-indigo-700",
    badge: "Real-time CV"
  },
  {
    id: 4,
    icon: Brain,
    label: "AI/ML Engine",
    short: "Inference",
    title: "Trained on 25,000+ Samples",
    desc: "Specialized models map color signatures to clinical-grade PCR & ACR values — secure cloud inference, results in seconds.",
    color: "violet",
    bg: "bg-violet-50",
    border: "border-violet-200",
    iconBg: "bg-violet-100",
    iconText: "text-violet-700",
    badge: "Cloud AI"
  },
  {
    id: 5,
    icon: FileCheck,
    label: "Your Result",
    short: "Report",
    title: "Lab-Grade Report in 90s",
    desc: "Cloud-synced kidney health report you can share with your doctor or download as PDF — anytime, anywhere.",
    color: "emerald",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    iconBg: "bg-emerald-100",
    iconText: "text-emerald-700",
    badge: "Doctor-shareable"
  }
];

const HIGHLIGHTS = [
  { icon: ScanSearch, label: "Auto-corrects lighting, angle & color cast" },
  { icon: Brain, label: "Trained on 25,000+ urine samples" },
  { icon: FlaskConical, label: "Patented reagent chemistry" },
  { icon: Wifi, label: "Secure cloud AI — results in 90 seconds" },
  { icon: Cloud, label: "Cloud-synced reports for your doctor" }
];

export const TechPipeline = () => (
  <section id="technology" data-testid="tech-pipeline-section" className="relative py-24 md:py-32 overflow-hidden">
    {/* Subtle techy backdrop */}
    <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-slate-50" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.07),transparent_60%)]" />
    <div className="absolute inset-0 bg-[linear-gradient(rgba(15,23,42,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.025)_1px,transparent_1px)] bg-[size:40px_40px]" />

    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      {/* Heading */}
      <div className="text-center max-w-3xl mx-auto mb-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-100 border border-indigo-200 mb-5">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          <span className="text-indigo-700 text-sm font-semibold tracking-wide">The Hidden Tech</span>
        </div>
        <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-800 mb-5">
          More Than <span className="gradient-text">Meets The Eye</span>
        </h2>
        <p className="text-slate-600 text-base md:text-lg leading-relaxed">
          What looks like a simple device is backed by <span className="text-slate-800 font-semibold">patented hardware</span>,
          <span className="text-slate-800 font-semibold"> computer vision</span>, and
          <span className="text-slate-800 font-semibold"> AI/ML algorithms</span> trained on 25,000+ samples.
        </p>
      </div>

      {/* Pipeline — Desktop horizontal */}
      <div className="hidden lg:block mb-12">
        <div className="flex items-stretch gap-2">
          {STAGES.map((stage, idx) => (
            <Fragment key={stage.id}>
              <div
                data-testid={`pipeline-stage-${stage.id}`}
                className={`flex-1 group relative ${stage.bg} ${stage.border} border-2 rounded-2xl p-5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 min-w-0`}
              >
                <div className={`w-12 h-12 rounded-xl ${stage.iconBg} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <stage.icon className={`w-6 h-6 ${stage.iconText}`} />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">
                  Stage {String(stage.id).padStart(2, '0')}
                </div>
                <div className={`font-semibold text-base ${stage.iconText} mb-1 leading-tight`}>{stage.label}</div>
                <div className="text-xs text-slate-500">{stage.short}</div>
                <div className={`absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-white ${stage.border} border text-[10px] font-bold ${stage.iconText} shadow-sm whitespace-nowrap`}>
                  {stage.badge}
                </div>
              </div>
              {idx < STAGES.length - 1 && (
                <div className="flex items-center justify-center flex-shrink-0 w-8">
                  <ArrowRight className="w-6 h-6 text-slate-400" />
                </div>
              )}
            </Fragment>
          ))}
        </div>
      </div>

      {/* Pipeline — Mobile vertical cards */}
      <div className="lg:hidden space-y-4 mb-12">
        {STAGES.map((stage, idx) => (
          <div key={stage.id} className="relative">
            <div className={`${stage.bg} ${stage.border} border-2 rounded-2xl p-5 flex gap-4 items-start`}>
              <div className={`w-12 h-12 rounded-xl ${stage.iconBg} flex items-center justify-center flex-shrink-0`}>
                <stage.icon className={`w-6 h-6 ${stage.iconText}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                    Stage {String(stage.id).padStart(2, '0')}
                  </div>
                  <div className={`px-2 py-0.5 rounded-full bg-white ${stage.border} border text-[9px] font-bold ${stage.iconText}`}>
                    {stage.badge}
                  </div>
                </div>
                <div className={`font-bold text-sm ${stage.iconText}`}>{stage.label}</div>
                <div className="text-xs text-slate-600 mt-1">{stage.desc}</div>
              </div>
            </div>
            {idx < STAGES.length - 1 && (
              <div className="flex justify-center py-1">
                <ArrowRight className="w-5 h-5 text-slate-300 rotate-90" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Detailed stage descriptions — Desktop */}
      <div className="hidden lg:grid grid-cols-5 gap-4 mb-14">
        {STAGES.map((stage) => (
          <div
            key={`desc-${stage.id}`}
            className="bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 hover:shadow-md transition-all"
          >
            <h4 className={`font-semibold text-sm ${stage.iconText} mb-2`}>{stage.title}</h4>
            <p className="text-xs text-slate-600 leading-relaxed">{stage.desc}</p>
          </div>
        ))}
      </div>

      {/* Tech Highlights Strip */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 md:p-10 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_right,_rgba(6,182,212,0.15),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_left,_rgba(139,92,246,0.12),transparent_60%)]" />

        <div className="relative">
          <div className="text-center mb-8">
            <h3 className="font-display text-xl md:text-2xl font-bold text-white mb-2">
              The Stack Behind 90-Second Kidney Diagnostics
            </h3>
            <p className="text-slate-400 text-sm">
              KidneyCheQ isn't a strip. It's a full diagnostic platform.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {HIGHLIGHTS.map((item) => (
              <div
                key={item.label}
                data-testid={`tech-highlight-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`}
                className="group flex flex-col items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-cyan-400/40 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500/20 to-indigo-500/20 border border-cyan-400/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <item.icon className="w-5 h-5 text-cyan-300" />
                </div>
                <p className="text-xs text-slate-200 leading-relaxed">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
